'use client'

import { useState } from 'react'

import * as BrandIcons from '@doist/icons/brand'
import { Box } from '@doist/marketist'

import { Quote } from 'app/components/custom-page/quote/quote'
import {
    isTypeTdCustomPageQuote,
    isTypeTdCustomPageRadioTileContainer,
    isTypeTdCustomPageTextLockUp,
    TypeTdCustomPageRadioTile,
} from 'app/services/contentful/models'
import { FeatureRadioGroup } from 'components/feature-radio-tile/feature-radio-group'
import { RadioTileScene, SceneWrapper } from 'components/feature-radio-tile/scene-images'
import { ProductUIThemeProvider } from 'components/product-ui/product-ui-theme-context'
import { ScrollHeader } from 'components/scrolling-section/scroll-header/scroll-header'
import { ScrollSection } from 'components/scrolling-section/scroll-section/scroll-section'
import merge from 'lodash/merge'

import { allScenes } from './all-scenes'
import { StaticImage } from './static-image'

import css from './scrolling-block.module.css'

import type {
    ButtonProps,
    QuoteProps,
    RadioTileContainerProps,
    ScrollingBlockProps,
    TextLockUpProps,
} from '../types'

import { CPButton } from '@/app/components/custom-page/button/button'
import { addProtocol } from '@/app/services/contentful/add-protocol-to-url'

type Tile = TypeTdCustomPageRadioTile<'WITHOUT_UNRESOLVABLE_LINKS', string>
type Trigger = { id: string; previousContainerId: string; nextContainerId: string }
type TileState = {
    tileIdForContainer: { [key: string]: string }
    currentTile: string
    items: { [key: string]: Tile }
}

function Scene(tile: Tile) {
    const { sceneName, backgroundVariant, staticImage } = tile.fields

    return sceneName === 'StaticImage' ? (
        <StaticImage
            backgroundVariant={backgroundVariant}
            src={addProtocol(staticImage?.fields.file?.url) ?? ''}
        />
    ) : (
        allScenes[sceneName]
    )
}

function ScrollingBlock(props: ScrollingBlockProps) {
    const { blocks = [], theme } = props.fields
    const [tiles, setTiles] = useState(assignInitialTileState(props))
    const [triggers] = useState<Trigger[]>(findScrollSectionTriggers(props))

    return (
        <ProductUIThemeProvider theme={theme}>
            <ScrollSection>
                <ScrollSection.StaticSection>
                    {blocks.map((block: any, i: number) => {
                        if (!block) return null
                        const { id } = block.sys
                        let element: JSX.Element | null = null

                        if (isTypeTdCustomPageTextLockUp(block)) {
                            element = <StaticHeader {...(block as TextLockUpProps)} />
                        }

                        if (isTypeTdCustomPageRadioTileContainer(block)) {
                            element = (
                                <Box className={css.radioGroupWrapper}>
                                    <RadioGroup
                                        onClick={(state: Partial<TileState>) => {
                                            setTiles((oldState) => {
                                                merge(oldState, state)
                                                return { ...oldState }
                                            })
                                        }}
                                        state={tiles}
                                        props={block as RadioTileContainerProps}
                                    />
                                    {block.fields.button ? (
                                        <CPButton {...(block.fields.button as ButtonProps)} />
                                    ) : null}
                                </Box>
                            )
                        }

                        if (isTypeTdCustomPageQuote(block)) {
                            element = <Quote {...(block as QuoteProps)} />
                        }

                        if (!element) return null

                        return (
                            <Wrapper
                                isLast={i === blocks.length - 1}
                                key={id}
                                id={id}
                                triggers={triggers}
                                onTrigger={(containerId: string) => {
                                    setTiles((state) => ({
                                        ...state,
                                        currentTile: state.tileIdForContainer[containerId],
                                    }))
                                }}
                            >
                                {element}
                            </Wrapper>
                        )
                    })}
                </ScrollSection.StaticSection>
                <ScrollSection.StickySection>
                    <SceneWrapper scene={<Scene {...tiles.items[tiles.currentTile]} />} />
                </ScrollSection.StickySection>
            </ScrollSection>
        </ProductUIThemeProvider>
    )
}

function assignInitialTileState({ fields: { blocks = [] } }: ScrollingBlockProps): TileState {
    const containers = blocks.filter(
        (b) => b !== undefined && isTypeTdCustomPageRadioTileContainer(b),
    )

    const state: TileState = {
        items: {},
        currentTile: '',
        tileIdForContainer: {},
    }

    containers.forEach((b, containerIndex: number) => {
        const { radioTiles = [] } = b.fields
        const tiles = radioTiles.filter((rt) => rt !== undefined)

        tiles.forEach((rt, tileIndex) => {
            // Assign the first tile to be the container's selected tile.
            if (tileIndex === 0) {
                if (containerIndex === 0) {
                    // currentTile sets the initial visible ProductUI scene.
                    state.currentTile = rt.sys.id
                }
                state.tileIdForContainer[b.sys.id] = rt.sys.id
            }

            // save tile props
            state.items[rt.sys.id] = rt
        })
    })

    return state
}

function findScrollSectionTriggers(props: ScrollingBlockProps): Trigger[] {
    const { blocks = [] } = props.fields

    const containers = blocks.flatMap((block, index: number) => {
        if (block && isTypeTdCustomPageRadioTileContainer(block)) {
            return [{ index, id: block.sys.id }]
        }
        return []
    })

    return containers.flatMap((c, i: number) => {
        // Ignore the first tile container as it doesn't have a trigger.
        const trigger = blocks[c.index - 1]
        if (i === 0 || !trigger) return []

        const { id } = trigger.sys
        const previousContainerId = containers[i - 1].id
        const nextContainerId = containers[i].id

        return [{ id, previousContainerId, nextContainerId }]
    })
}

function StaticHeader({ fields: { title, subtitle } }: TextLockUpProps) {
    return <ScrollHeader title={title}>{subtitle}</ScrollHeader>
}

type RadioGroupProps = {
    props: RadioTileContainerProps
    onClick: (state: Partial<TileState>) => void
    state: TileState
}

function RadioGroup({ props, onClick, state }: RadioGroupProps) {
    const { radioTiles = [] } = props.fields
    const { id } = props.sys

    const items = radioTiles.flatMap((tile) => {
        if (!tile) return []

        const { title, description, icon } = tile.fields

        const iconName = icon?.fields.iconName || 'Activity'
        // eslint-disable-next-line import/namespace
        const Icon = BrandIcons[iconName as keyof typeof BrandIcons]

        return [
            {
                titleKey: title,
                bodyKey: description,
                // @ts-expect-error TODO: fix this
                icon: <Icon />,
                scene: <RadioTileScene scene={<Scene {...tile} />} />,
                sceneName: tile.sys.id,
            },
        ]
    })

    function radioTileChangeHandler(value: string) {
        const sceneId = value
        onClick({ currentTile: sceneId, tileIdForContainer: { [id]: sceneId } })
    }

    return (
        <FeatureRadioGroup<string>
            onChange={radioTileChangeHandler}
            value={state.tileIdForContainer[id]}
            radioTiles={items}
        />
    )
}

function Wrapper({
    children,
    id,
    isLast,
    triggers,
    onTrigger,
}: {
    children: React.ReactNode
    id: string
    isLast: boolean
    triggers: Trigger[]
    onTrigger: (containerId: string) => void
}) {
    if (isLast) {
        // The last block should be a sticky anchor.
        return <ScrollSection.StickyAnchor>{children}</ScrollSection.StickyAnchor>
    }

    const trigger = triggers.find((t) => t.id === id)
    if (trigger) {
        return (
            <ScrollSection.Trigger
                onEnter={() => onTrigger(trigger.nextContainerId)}
                onEnterBack={() => onTrigger(trigger.previousContainerId)}
            >
                {children}
            </ScrollSection.Trigger>
        )
    }

    return <>{children}</>
}

export { ScrollingBlock }
